import {get, isArray, isUndefined, noop} from 'lodash';
import {useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {
  getBusinessDetailFormConfig,
  overWriteBusinessDetailsFields,
} from 'src/CpaCenterV2/common/EditBusinessDetails/EditBusinesDetails.utils';
import {useTaxProfile} from 'src/CpaCenterV2/hooks/useTaxProfile';
import {FormConfigProps} from 'src/DesignSystem/Form/Form.types';
import {
  checkFormDataValidity,
  getParsedFields,
  parseFormConfigToFormData,
} from 'src/DesignSystem/Form/Form.utils';
import {OverWriteFormFieldsFunctionArgsType} from 'src/DesignSystem/Form/useFormData';
import {PartnerCreditsSubsectionConfig} from 'src/Partnership/PartnerReturnInfo/PartnerTaxProfileReview/PartnerTaxProfileSections/PartnerCredits/PartnerCredits.constants';
import {overWriteTaxProfileFields} from 'src/common/EditTaxProfile/EditTaxProfile.utils';
import {useActiveYear} from 'src/common/hooks/useActiveYear';
import {useTaxonomy} from 'src/common/hooks/useTaxonomy';
import {getPath} from 'src/common/utils';
import {
  PartnerTaxProfileSectionSequence,
  TaxProfileSection,
  PartnerTaxProfileSectionToSubsectionMapping,
  TaxProfileSubsection,
} from 'src/constants/constants';
import {
  setEditTaxForm,
  setEditTaxProfileQuestions,
} from 'src/store/app/app.actions';
import {selectBalanceSheetReducer} from 'src/store/balanceSheet/balanceSheet.selector';
import {selectBusinessDetails} from 'src/store/businessDetails/businessDetails.selector';
import {BusinessDetailsField} from 'src/store/businessDetails/businessDetails.types';
import {selectTaxFormsReducer} from 'src/store/taxForms/taxForms.selector';
import {
  BalanceSheetType,
  TaxForm,
  TaxFormFieldIds,
  TaxFormType,
} from 'src/store/taxForms/taxForms.types';
import {
  filterProfitAndLossExpenseField,
  filterProfitAndLossIncomeField,
  getEditTaxFormConfig,
  getOverwriteTaxForm,
  validateBalanceSheet,
  validateProfitLoss,
} from 'src/store/taxForms/taxForms.utils';
import {
  FILING_STATUS_ANSWER,
  TaxProfileField,
  TaxProfileQuestion,
} from 'src/store/taxProfile/taxProfile.types';
import {getConfigForTaxProfileQuestions} from 'src/store/taxProfile/taxProfile.utils';
import {TaxProfileReviewStatus} from 'src/store/taxProfileStatus/taxProfileStatus.reducer';
import {selectTaxProfileCpaSideStatus} from 'src/store/taxProfileStatus/taxProfileStatus.selector';
import {
  TaxReturnField,
  TaxReturnType,
} from 'src/store/taxReturns/taxReturns.reducer';
import {selectTaxReturnsReducer} from 'src/store/taxReturns/taxReturns.selector';

export const usePartnerTaxProfileReview = () => {
  const {taxProfileCpaSideStatus, loaded} = useSelector(
    selectTaxProfileCpaSideStatus,
  );
  const {taxReturns} = useSelector(selectTaxReturnsReducer);
  const {taxForms} = useSelector(selectTaxFormsReducer);
  const {businesses} = useSelector(selectBusinessDetails);
  const dispatch = useDispatch();
  const {irsCategories, getL2s} = useTaxonomy();
  const {balance_sheet_data} = useSelector(selectBalanceSheetReducer);

  const {activeYear} = useActiveYear();

  const isFilingPersonalReturn = useMemo(() => {
    const personalReturn = taxReturns.filter(
      (t) => t[TaxReturnField.return_type] === TaxReturnType.INDIVIDUAL,
    )?.[0];
    if (personalReturn && personalReturn[TaxReturnField.should_file_return]) {
      return true;
    }
    return false;
  }, [taxReturns]);

  const {taxProfileMap} = useTaxProfile();

  const getPreviousSection = (section: TaxProfileSection) => {
    const sectionIndex = PartnerTaxProfileSectionSequence.findIndex(
      (item) => item.section === section,
    );
    if (sectionIndex === 0) {
      return null;
    }
    return PartnerTaxProfileSectionSequence[sectionIndex - 1];
  };

  const getNextSection = (section: TaxProfileSection) => {
    const sectionIndex = PartnerTaxProfileSectionSequence.findIndex(
      (item) => item.section === section,
    );
    if (sectionIndex === PartnerTaxProfileSectionSequence.length - 1) {
      return null;
    }
    return PartnerTaxProfileSectionSequence[sectionIndex + 1];
  };

  const isSectionReviewPending = (section: TaxProfileSection) => {
    const allSubsections =
      PartnerTaxProfileSectionToSubsectionMapping[section];
    const isPending = allSubsections.some(
      (subsection) => !isSubsectionReviewComplete(subsection),
    );
    return isPending;
  };

  const isSubSectionVisible = (subsection: TaxProfileSubsection) => {
    const filingStatus = taxProfileMap[TaxProfileQuestion.FILING_STATUS];
    const selfSomeoneDependent =
      taxProfileMap[TaxProfileQuestion.CLAIM_YOU_DEPENDENT];
    const spouseSomeoneElseDependent =
      taxProfileMap[TaxProfileQuestion.SPOUSE_CLAIMED_DEPENDENT];

    if (
      subsection === TaxProfileSubsection.SpouseDetails &&
      ([
        FILING_STATUS_ANSWER.SINGLE,
        FILING_STATUS_ANSWER.HEAD_OF_HOUSEHOLD,
      ].includes(filingStatus) ||
        !isFilingPersonalReturn)
    ) {
      return false;
    }
    if (
      subsection === TaxProfileSubsection.DependentDetails &&
      (!isFilingPersonalReturn ||
        selfSomeoneDependent ||
        spouseSomeoneElseDependent)
    ) {
      return false;
    }
    return true;
  };

  const isSubsectionVisibleAndReviewComplete = (
    subsection: TaxProfileSubsection,
  ) => {
    return (
      isSubSectionVisible(subsection) &&
      taxProfileCpaSideStatus[subsection] === TaxProfileReviewStatus.COMPLETE
    );
  };

  const isSomeSubsectionReviewComplete = (section: TaxProfileSection) => {
    const allSubsections =
      PartnerTaxProfileSectionToSubsectionMapping[section];
    return allSubsections.some(isSubsectionVisibleAndReviewComplete);
  };

  const getSubsectionTaxProfileQuestion = (
    subsection: TaxProfileSubsection,
  ) => {
    const filingStatus = taxProfileMap[TaxProfileQuestion.FILING_STATUS];
    let questions: (TaxProfileQuestion | string)[] = [];
    if (subsection === TaxProfileSubsection.UserDetails) {
      questions = [
        TaxProfileQuestion.LEGAL_NAME,
        TaxProfileQuestion.SSN,
        TaxProfileQuestion.DOB,
        TaxProfileQuestion.FILING_STATUS,
        TaxProfileQuestion.CLAIM_YOU_DEPENDENT,
        TaxProfileQuestion.DEPENDENT,
        TaxProfileQuestion.IS_ELIGIBLE_FOR_EDUCATION_CREDIT,
      ];
    }
    if (subsection === TaxProfileSubsection.HomeAddress) {
      questions = [
        TaxProfileQuestion.LIVING_OUTSIDE_USA,
        getPath(
          TaxProfileQuestion.HOME_ADDRESS,
          TaxProfileField.STREET_NUMBER,
        ),
        getPath(
          TaxProfileQuestion.HOME_ADDRESS,
          TaxProfileField.APARTMENT_NUMBER,
        ),
        getPath(TaxProfileQuestion.HOME_ADDRESS, TaxProfileField.CITY),
        getPath(TaxProfileQuestion.HOME_ADDRESS, TaxProfileField.STATE),
        getPath(TaxProfileQuestion.HOME_ADDRESS, TaxProfileField.ZIP_CODE),
        getPath(
          TaxProfileQuestion.HOME_ADDRESS,
          TaxProfileField.SCHOOL_DISTRICT_CODE,
        ),
        getPath(TaxProfileQuestion.HOME_ADDRESS, TaxProfileField.COUNTY),
        TaxProfileQuestion.MAILING_ADDRESS_OUTSIDE_USA,
      ];
    }
    if (subsection === TaxProfileSubsection.SpouseDetails) {
      questions = [
        TaxProfileQuestion.SPOUSE_LEGAL_NAME,
        TaxProfileQuestion.SPOUSE_SSN,
      ];
      if (filingStatus !== FILING_STATUS_ANSWER.MARRIED_FILING_SEPARAELY) {
        questions.push(
          ...[
            TaxProfileQuestion.SPOUSE_DOB,
            TaxProfileQuestion.SPOUSE_EMAIL,
            TaxProfileQuestion.SPOUSE_CLAIMED_DEPENDENT,
            TaxProfileQuestion.IS_SPOUSE_ELIGIBLE_FOR_EDUCATION_CREDIT,
          ],
        );
      }
    }
    return questions.map((q) => getPath(q));
  };

  const isSubsectionComplete = (subsection: TaxProfileSubsection) => {
    if (!isSubSectionVisible(subsection)) {
      // if not visible then treat it as complete
      return true;
    }
    const filingStatus = taxProfileMap[TaxProfileQuestion.FILING_STATUS];
    const pureTaxProfileSubsections = [
      TaxProfileSubsection.UserDetails,
      TaxProfileSubsection.HomeAddress,
      TaxProfileSubsection.SpouseDetails,
    ];
    if (pureTaxProfileSubsections.includes(subsection)) {
      const taxProfieQuestions = getSubsectionTaxProfileQuestion(subsection);
      const config = getConfigForTaxProfileQuestions(taxProfieQuestions);
      const data = parseFormConfigToFormData(config, taxProfileMap);
      const fields = getParsedFields(
        config,
        data,
        noop,
        overWriteTaxProfileFields,
      );
      const areAllValid = checkFormDataValidity(fields);
      return areAllValid;
    }

    if (subsection === TaxProfileSubsection.DependentDetails) {
      const dependentTaxForms = taxForms.filter(
        (tf) => tf.formType === TaxFormType.DEPENDENT,
      ) as TaxForm<TaxFormType.DEPENDENT>[];
      if (
        filingStatus === FILING_STATUS_ANSWER.HEAD_OF_HOUSEHOLD &&
        dependentTaxForms.length === 0
      ) {
        return false;
      }
      const config = getEditTaxFormConfig(TaxFormType.DEPENDENT);
      return dependentTaxForms.every((dep) => {
        const fields = getParsedFields(config, dep.formData, noop);
        return checkFormDataValidity(fields);
      });
    }

    if (subsection === TaxProfileSubsection.BusinessDetails) {
      return businesses.every((biz) => {
        const config = getBusinessDetailFormConfig(
          biz[BusinessDetailsField.have_ein],
          biz[BusinessDetailsField.entity_type],
        );
        const fields = getParsedFields(config, biz, noop, (props) =>
          overWriteBusinessDetailsFields({...props, filingStatus}),
        );
        return checkFormDataValidity(fields);
      });
    }

    if (
      subsection === TaxProfileSubsection.DependentCredits ||
      subsection === TaxProfileSubsection.EducationalCredits ||
      subsection === TaxProfileSubsection.AdoptionCredits
    ) {
      const config = PartnerCreditsSubsectionConfig[subsection];
      const tpQuestions = config.taxProfileQuestions;
      const field = config.field;
      const dependentTaxForms = taxForms.filter(
        (tf) => tf.formType === TaxFormType.DEPENDENT,
      ) as TaxForm<TaxFormType.DEPENDENT>[];
      const allDependentCreditsAnswered = dependentTaxForms.every(
        (dep) => typeof dep.formData[field] === 'boolean',
      );

      const allCreditsTPQuestionsAnswered = tpQuestions.every((tp) => {
        if (
          tp === TaxProfileQuestion.IS_SPOUSE_ELIGIBLE_FOR_EDUCATION_CREDIT ||
          filingStatus !== FILING_STATUS_ANSWER.MARRIED_FILING_JOINTLY
        ) {
          return true;
        }
        return typeof taxProfileMap[tp] === 'boolean';
      });

      return allDependentCreditsAnswered && allCreditsTPQuestionsAnswered;
    }

    if (subsection === TaxProfileSubsection.StatesTaxFilingInformation) {
      const livingOutsideUSA = get(
        taxProfileMap,
        TaxProfileQuestion.LIVING_OUTSIDE_USA,
        undefined,
      );
      if (!isSubsectionComplete(TaxProfileSubsection.HomeAddress)) {
        // if home address not complete then staes will be incomplete as well
        return false;
      }

      if (livingOutsideUSA === false) {
        const tpPeriods = get(
          taxProfileMap,
          getPath(TaxProfileQuestion.HOME_ADDRESS, TaxProfileField.PERIODS),
          undefined,
        ) as undefined | Array<any>;
        if (!isArray(tpPeriods) || tpPeriods.length === 0) {
          return false;
        }
      }
    }

    if (subsection === TaxProfileSubsection.BusinessIncome) {
      return businesses.every((biz) => {
        const config = getEditTaxFormConfig(
          TaxFormType.PROFIT_LOSS_FORM,
          filterProfitAndLossIncomeField,
        );
        const linkedTf = taxForms.find(
          (tf) =>
            tf.linked_business_id === biz[BusinessDetailsField.id] &&
            tf.formType === TaxFormType.PROFIT_LOSS_FORM,
        );
        if (isUndefined(linkedTf)) {
          return true;
        }

        const fields = getParsedFields(
          config,
          linkedTf.formData,
          noop,
          (props) => {
            return getOverwriteTaxForm(TaxFormType.PROFIT_LOSS_FORM, {
              taxforms: taxForms,
              taxprofileMap: taxProfileMap,
              businesses,
            })(props);
          },
        );
        return checkFormDataValidity(fields);
      });
    }

    if (subsection === TaxProfileSubsection.BusinessDeductions) {
      return businesses.every((biz) => {
        const config = getEditTaxFormConfig(
          TaxFormType.PROFIT_LOSS_FORM,
          filterProfitAndLossExpenseField,
        );
        const linkedTf = taxForms.find(
          (tf) =>
            tf.linked_business_id === biz[BusinessDetailsField.id] &&
            tf.formType === TaxFormType.PROFIT_LOSS_FORM,
        );
        if (isUndefined(linkedTf)) {
          return true;
        }

        const overWriteFields = (
          props: OverWriteFormFieldsFunctionArgsType,
        ) => {
          return getOverwriteTaxForm(TaxFormType.PROFIT_LOSS_FORM, {
            taxforms: taxForms,
            taxprofileMap: taxProfileMap,
            businesses,
          })(props);
        };
        return validateProfitLoss({
          config,
          taxFormData: linkedTf.formData,
          setTaxFormData: noop,
          businesses,
          linkedBusinessId: linkedTf.linked_business_id,
          irsCategories,
          getL2s,
          overWriteFields,
        });
      });
    }

    if (subsection === TaxProfileSubsection.BusinessBalanceSheet) {
      return balance_sheet_data.every((balanceSheet) => {
        const config = getEditTaxFormConfig(TaxFormType.BALANCE_SHEET_FORM);
        const startTf = taxForms.find(
          (tf) => balanceSheet.year_start_form_id === tf.taxFormId,
        );
        const endTF = taxForms.find(
          (tf) => balanceSheet.year_end_form_id === tf.taxFormId,
        );
        if (isUndefined(startTf) || isUndefined(endTF)) {
          return true;
        }

        return (
          validateBalanceSheet({
            config,
            taxFormData: startTf.formData,
            setTaxFormData: noop,
            overWriteFields: getOverwriteTaxForm(
              TaxFormType.BALANCE_SHEET_FORM,
              {taxforms: taxForms, taxprofileMap: taxProfileMap, businesses},
            ),
            businesses,
            linkedBusinessId: startTf.linked_business_id,
          }) &&
          validateBalanceSheet({
            config,
            taxFormData: endTF.formData,
            setTaxFormData: noop,
            overWriteFields: getOverwriteTaxForm(
              TaxFormType.BALANCE_SHEET_FORM,
              {taxforms: taxForms, taxprofileMap: taxProfileMap, businesses},
            ),
            businesses,
            linkedBusinessId: endTF.linked_business_id,
          })
        );
      });
    }

    return true;
  };

  const isSectionComplete = (section: TaxProfileSection) => {
    const allSubsections =
      PartnerTaxProfileSectionToSubsectionMapping[section];
    return allSubsections.every(isSubsectionComplete);
  };

  const onEditTaxProfileSubsection = (subsection: TaxProfileSubsection) => {
    const tpQuestions = getSubsectionTaxProfileQuestion(subsection);
    dispatch(
      setEditTaxProfileQuestions({
        pathPrefixes: tpQuestions,
        title: subsection,
      }),
    );
  };

  const onEditTaxForm = ({
    taxFormId,
    taxFormType,
    title,
    prefilledFormData = {},
    filterFields,
    onCommit,
  }: {
    taxFormId: number;
    taxFormType: TaxFormType;
    title: string;
    prefilledFormData?: any;
    filterFields?: (field: FormConfigProps) => boolean;
    onCommit?: () => void;
  }) => {
    dispatch(
      setEditTaxForm({
        isVisible: true,
        taxFormId,
        taxFormType,
        title,
        prefilledFormData,
        filterFields,
        onCommit,
      }),
    );
  };

  const onAddTaxForm = ({
    taxFormType,
    title,
    prefilledFormData = {},
    multi_tax_forms_entity_id,
    filterFields,
  }: {
    taxFormType: TaxFormType;
    title: string;
    prefilledFormData?: any;
    multi_tax_forms_entity_id?: number;
    filterFields?: (field: FormConfigProps) => boolean;
  }) => {
    dispatch(
      setEditTaxForm({
        isVisible: true,
        taxFormId: null,
        taxFormType,
        title,
        prefilledFormData,
        multi_tax_forms_entity_id,
        filterFields,
      }),
    );
  };

  const isSubsectionReviewComplete = (subsection: TaxProfileSubsection) => {
    return (
      taxProfileCpaSideStatus[subsection] ===
        TaxProfileReviewStatus.COMPLETE || !isSubSectionVisible(subsection)
    );
  };
  const isSubSectionPendingFromUser = (subsection: TaxProfileSubsection) => {
    return false;
  };

  return {
    isSectionReviewPending,
    isSomeSubsectionReviewComplete,
    getNextSection,
    getPreviousSection,
    loaded,
    onEditTaxProfileSubsection,
    onEditTaxForm,
    onAddTaxForm,
    isSubsectionReviewComplete,
    isSubSectionPendingFromUser,
    isSubSectionVisible,
    isSubsectionComplete,
    isSectionComplete,
  };
};
